import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import type { PaginatedResponse, Run } from "../lib/types.js";

interface RunFailure {
  projectName: string;
  runId: string;
  since: string;
  checkSlug: string;
  checkName: string;
  category: string;
  url: string;
  detail: string | null;
}

export function useRuns(projectId: string, page = 1) {
  return useQuery({
    queryKey: ["runs", projectId, page],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Run>>(
        `/projects/${projectId}/runs?page=${page}&limit=20`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useRunDetail(runId: string) {
  return useQuery({
    queryKey: ["run", runId],
    queryFn: async () => {
      const { data } = await api.get<Run>(`/runs/${runId}`);
      return data;
    },
    enabled: !!runId,
  });
}

export function useLatestRuns() {
  return useQuery({
    queryKey: ["runs", "latest"],
    queryFn: async () => {
      const { data } = await api.get<{ projectId: string; projectName: string; run: Run | null }[]>("/runs/latest");
      // Unwrap: flatten to Run[] with projectName merged in, skip projects with no run
      return data
        .filter((item) => item.run !== null)
        .map((item) => ({ ...item.run!, projectName: item.projectName }));
    },
  });
}

export function useRunHistory() {
  return useQuery({
    queryKey: ["runs", "history"],
    queryFn: async () => {
      const { data } = await api.get<Run[]>("/runs/history");
      return data;
    },
  });
}

export function useRunFailures() {
  return useQuery({
    queryKey: ["runs", "failures"],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<RunFailure>>("/runs/failures");
      return data.data;
    },
  });
}
